/**
 * The maze, and the symmetry that makes the race fair.
 *
 * This file has a twin: `app/lib/maze.dart`. They must produce byte-identical
 * mazes for every seed, because the server validates moves against its own copy
 * of the maze while the player is looking at the client's. `shared/golden-
 * vectors.json` is generated by the Dart side and asserted by the TypeScript
 * side, so a divergence fails CI rather than desyncing a live game. If you
 * change anything here, change `maze.dart` in the same commit and regenerate
 * the vectors.
 *
 * ## Why 180-degree rotation and not a diagonal reflection
 *
 * The requirement is that the two entrances — one on the top edge, one on the
 * bottom — pose exactly the same problem. That fixes the symmetry completely,
 * and it is not either diagonal.
 *
 * Reflecting in the main diagonal sends (r,c) to (c,r), so a top-edge cell
 * (0,c) lands on the *left* edge at (c,0). The anti-diagonal sends it to the
 * right edge. Neither maps the top edge to the bottom edge, so neither can
 * relate the two entrances at all. A maze symmetric in *both* diagonals is
 * symmetric under their composition — which is the 180-degree rotation — but
 * demanding both would also force an entrance on all four edges, and the game
 * has two players.
 *
 * So the symmetry used here is the point reflection through the centre:
 *
 *     R(r, c) = (size-1-r, size-1-c)
 *
 * It fixes the centre, swaps the two entrances, and maps the maze onto itself.
 * That is the whole fairness argument: R carries one player's unique route onto
 * the other's, so the two routes are the same length and the same shape, turn
 * for turn. `tests/maze.test.ts` asserts exactly that.
 */

/** Directions, in the order their bits appear in `Maze.open`. */
export const NORTH = 0;
export const EAST = 1;
export const SOUTH = 2;
export const WEST = 3;

/** Row delta per direction, indexed by the constants above. */
export const DR = [-1, 0, 1, 0];
/** Column delta per direction. */
export const DC = [0, 1, 0, -1];

/** The direction you came from, if you left going `d`. */
export const opposite = (d: number): number => (d + 2) % 4;

/**
 * SplitMix32.
 *
 * Hand-rolled rather than taken from a library because the Dart twin has to
 * produce the identical stream, and "whatever `Random(seed)` does this release"
 * is not a specification. Every step is masked back to 32 bits explicitly;
 * `Math.imul` is what keeps the multiplies exact, since an ordinary `*` on two
 * 32-bit operands overflows the 53 bits a double can hold.
 */
export class SplitMix32 {
  private s: number;

  constructor(seed: number) {
    this.s = seed >>> 0;
  }

  next(): number {
    this.s = (this.s + 0x9e3779b9) >>> 0;
    let z = this.s;
    z = Math.imul(z ^ (z >>> 16), 0x21f0aaad) >>> 0;
    z = Math.imul(z ^ (z >>> 15), 0x735a2d97) >>> 0;
    return (z ^ (z >>> 15)) >>> 0;
  }

  /**
   * Uniform-ish in [0, n). The modulo bias is real but bounded by n/2^32 and
   * n is never more than 4 here; what matters far more is that Dart computes
   * the same thing, so this stays a plain remainder.
   */
  nextInt(n: number): number {
    return this.next() % n;
  }
}

export interface Maze {
  /** Cells per side. Always odd — see `generate`. */
  size: number;
  /**
   * One bitmask per cell, row-major: bit `d` is set when the wall in direction
   * `d` is *open*. Walls are stored on both sides, so `open` is self-consistent
   * and a move can be checked from either cell.
   */
  open: Uint8Array;
  /** The top entrance is at (0, entranceCol); the bottom is its image under R. */
  entranceCol: number;
}

/** Index of (r, c) in row-major order. */
export const idx = (size: number, r: number, c: number): number => r * size + c;

/**
 * The 180-degree rotation, on indices.
 *
 * (r,c) -> (size-1-r, size-1-c) is, in row-major terms, i -> size*size-1-i.
 */
export const rotate = (size: number, i: number): number => size * size - 1 - i;

/** The canonical member of {i, R(i)}; the rotation's orbits are keyed by this. */
export const orbit = (size: number, i: number): number =>
  Math.min(i, rotate(size, i));

/** The centre cell, which is the goal and the one cell R holds still. */
export const centerIndex = (size: number): number => (size * size - 1) / 2;

/** Where the top player starts. */
export const topStart = (m: Maze): number => m.entranceCol;

/** Where the bottom player starts: the top entrance, rotated. */
export const bottomStart = (m: Maze): number =>
  rotate(m.size, m.entranceCol);

/** True when you can step from cell `i` in direction `d` without crossing a wall. */
export function canMove(m: Maze, i: number, d: number): boolean {
  if ((m.open[i] & (1 << d)) === 0) return false;
  // The bit alone would be enough for an interior wall, but the outer boundary
  // is never carved, so this is belt-and-braces against a corrupt mask.
  const r = Math.floor(i / m.size) + DR[d];
  const c = (i % m.size) + DC[d];
  return r >= 0 && r < m.size && c >= 0 && c < m.size;
}

/** The cell you arrive at. Call only when `canMove` agreed. */
export function step(m: Maze, i: number, d: number): number {
  return idx(m.size, Math.floor(i / m.size) + DR[d], (i % m.size) + DC[d]);
}

function carve(open: Uint8Array, size: number, a: number, d: number): void {
  const b = a + DR[d] * size + DC[d];
  const od = opposite(d);
  open[a] |= 1 << d;
  open[b] |= 1 << od;
  // The same passage, rotated. R reverses every direction, so the partner of
  // an edge leaving `a` northward is an edge leaving R(a) southward. Carving
  // the pair here — rather than mirroring afterwards — is what makes the
  // symmetry an invariant of the generator instead of a post-condition.
  const ra = rotate(size, a);
  const rb = rotate(size, b);
  open[ra] |= 1 << od;
  open[rb] |= 1 << d;
}

/**
 * A perfect maze — exactly one route between any two cells — that is invariant
 * under R.
 *
 * The trick is to run an ordinary randomised depth-first carve on the *quotient*
 * graph, where a cell and its rotation are one node, and to lift every carved
 * edge back to both of its preimages. Two facts make that come out exactly
 * right, and both depend on `size` being odd:
 *
 *  - **It stays connected.** Lifting a spanning tree of a quotient generally
 *    gives a double cover, which can fall into two pieces. It cannot here,
 *    because the centre is a fixed point of R and so is a single cell: every
 *    lifted path from anywhere ends at that one cell, which fuses the sheets.
 *
 *  - **It stays a tree.** No edge is its own rotation. An edge could only be
 *    self-paired if R swapped its endpoints, which needs `2c = size-1 ± 1` on
 *    the middle row — even on the left, odd on the right, so there is no
 *    solution. Every one of the (size²-1)/2 quotient edges therefore lifts to
 *    two distinct edges, giving size²-1 edges over size² cells: a spanning tree.
 *
 * An even `size` breaks both arguments, which is why it is rejected rather than
 * rounded.
 */
export function generate(seed: number, size: number): Maze {
  if (size % 2 === 0) {
    throw new Error(`maze size must be odd, got ${size}`);
  }
  if (size < 3) {
    throw new Error(`maze size must be at least 3, got ${size}`);
  }

  const rng = new SplitMix32(seed);
  const cells = size * size;
  const open = new Uint8Array(cells);

  // Indexed by orbit representative; the other half of each orbit is never
  // written, which is harmless and keeps the indexing trivial.
  const seen = new Uint8Array(cells);

  const start = centerIndex(size);
  const stack: number[] = [start];
  seen[start] = 1;

  const candidates: number[] = [];
  while (stack.length > 0) {
    const u = stack[stack.length - 1];
    const r = Math.floor(u / size);
    const c = u % size;

    candidates.length = 0;
    for (let d = 0; d < 4; d++) {
      const nr = r + DR[d];
      const nc = c + DC[d];
      if (nr < 0 || nr >= size || nc < 0 || nc >= size) continue;
      if (seen[orbit(size, idx(size, nr, nc))]) continue;
      candidates.push(d);
    }

    if (candidates.length === 0) {
      stack.pop();
      continue;
    }

    const d = candidates[rng.nextInt(candidates.length)];
    carve(open, size, u, d);
    // Walk on in the quotient, so the next frame is a representative again.
    stack.push(orbit(size, idx(size, r + DR[d], c + DC[d])));
    seen[stack[stack.length - 1]] = 1;
  }

  // Drawn as a gap in the outer wall; the players stand on these two cells.
  // Taken from the same stream as the carve so one seed fixes the whole board.
  const entranceCol = rng.nextInt(size);

  return { size, open, entranceCol };
}

/**
 * The route from `from` to the centre, inclusive at both ends.
 *
 * The maze is a tree, so this route is unique and a depth-first walk finds it
 * without any need to compare lengths.
 */
export function solve(m: Maze, from: number): number[] {
  const goal = centerIndex(m.size);
  const prev = new Int32Array(m.size * m.size).fill(-1);
  const stack = [from];
  prev[from] = from;

  while (stack.length > 0) {
    const u = stack.pop()!;
    if (u === goal) break;
    for (let d = 0; d < 4; d++) {
      if (!canMove(m, u, d)) continue;
      const v = step(m, u, d);
      if (prev[v] !== -1) continue;
      prev[v] = u;
      stack.push(v);
    }
  }

  if (prev[goal] === -1) return [];
  const path: number[] = [];
  for (let u = goal; u !== from; u = prev[u]) path.push(u);
  path.push(from);
  return path.reverse();
}
